// Simulator-specific types
import { NgapAct } from '../../../types/ngap';

// Balneotherapy types
export type BalneotherapyType = 'none' | 'pool' | 'basin';

export interface SimulatorCartItem {
  id: string; // Unique identifier for the cart item
  act: NgapAct;
  quantity: number; // Always stored as daily equivalent (acts per day)
  homeVisitPercentage: number; // Percentage of home visits (0-100)
  balneotherapy: BalneotherapyType; // Type of balneotherapy supplement
}

export type TimePeriod = 'day' | 'week' | 'month' | 'year';

export interface RevenueCalculation {
  currentRevenue: number;
  futureRevenue: number;
  difference: number;
  percentageChange: number;
}

export interface SimulatorSummary {
  totalOriginalRevenue: number;
  totalCurrentRevenue: number;
  totalFutureRevenueJanv2026: number;
  totalDifferenceJanv2026: number;
  totalPercentageChangeJanv2026: number;
  totalFutureRevenueMay2026: number;
  totalDifferenceMay2026: number;
  totalPercentageChangeMay2026: number;
  totalFutureRevenueSept2026: number;
  totalDifferenceSept2026: number;
  totalPercentageChangeSept2026: number;
  totalFutureRevenue2027: number;
  totalDifference2027: number;
  totalPercentageChange2027: number;
  itemCount: number;
  hasOriginalValues: boolean;
  totalHomeVisitRevenue: number;
  actsWithHomeVisitsCount: number;
  totalQuantityInSelectedPeriod: number;
}

export interface SimulatorState {
  cartItems: SimulatorCartItem[];
  selectedPeriod: TimePeriod;
  selectedActId: string | null;
  isDrom: boolean;
  customMultipliers: CustomPeriodMultipliers;
}

// Period multipliers for calculations
export const PERIOD_MULTIPLIERS = {
  day: 1,
  week: 5, // 5 working days
  month: 20, // Average working days per month
  year: 240 // Average working days per year (20 days/month * 12 months)
} as const;

// Custom period multipliers interface
export interface CustomPeriodMultipliers {
  day: number;
  week: number;
  month: number;
  year: number;
}

// Custom point values interface
export interface CustomPointValues {
  metropole: number;
  drom: number;
}

// Period descriptions for UI
export const PERIOD_DESCRIPTIONS = {
  day: 'par jour',
  week: 'par semaine', 
  month: 'par mois',
  year: 'par année'
} as const;

// Period labels for UI
export const PERIOD_LABELS = {
  day: 'Jour',
  week: 'Semaine', 
  month: 'Mois',
  year: 'Année'
} as const;

// Default quantities for different periods (what users typically enter)
export const DEFAULT_QUANTITIES = {
  day: 1,
  week: 5,
  month: 20,
  year: 240
} as const;

// Suggested placeholder values for different periods (natural integers only)
export const QUANTITY_PLACEHOLDERS = {
  day: 'Ex: 3',
  week: 'Ex: 15',
  month: 'Ex: 60',
  year: 'Ex: 720'
} as const;

// Limits for period multipliers
export const PERIOD_MULTIPLIER_LIMITS = {
  day: { min: 1, max: 1 }, // Day is always 1
  week: { min: 1, max: 7 }, // 1 to 7 days per week
  month: { min: 1, max: 31 }, // 1 to 31 days per month
  year: { min: 100, max: 365 } // 100 to 365 days per year
} as const;

// Balneotherapy tariffs (Avenant 7)
export const BALNEOTHERAPY_TARIFFS = {
  2025: {
    pool: 4.87,   // En piscine - 2025
    basin: 2.65   // En bassin - 2025
  },
  2026: {
    pool: 7.74,   // En piscine - 2026 et après (revalorisation de 2,87€)
    basin: 5.52   // En bassin - 2026 et après (revalorisation de 2,87€)
  }
} as const;

// Balneotherapy labels for UI
export const BALNEOTHERAPY_LABELS = {
  none: 'Aucune',
  pool: 'Piscine',
  basin: 'Bassin'
} as const;